import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../api/axiosInstance";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);

    const buildUserFromToken = (token) => {
        if (!token) return null;
        try {
            const decoded = jwtDecode(token);
            return {
                username: decoded?.userName || null,

                exp: decoded?.exp || null,
                token,
            };
        } catch (e) {
            return null;
        }
    };

    useEffect(() => {
        async function initializeAuth() {
            const token = localStorage.getItem("authToken");

            if (!token) {
                setInitializing(false);
                return;
            }
            const userFromToken = buildUserFromToken(token);
            if (!userFromToken || userFromToken.exp < Math.floor(Date.now() / 1000)) {
                localStorage.removeItem("authToken");
                setUser(null);
                setInitializing(false);
                return;
            }
            setUser(userFromToken);
            setInitializing(false);
        }

        initializeAuth();
        const onStorage = (storageEvent) => {
            if (storageEvent.key === "authToken") {
                const newToken = storageEvent.newValue;
                if (newToken) {
                    setUser(buildUserFromToken(newToken));
                } else {
                    setUser(null);
                }
            }
        };

        window.addEventListener("storage", onStorage);

        return () => {
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const login = useCallback(async (username, password) => {
        setLoading(true);
        localStorage.removeItem("authToken");
        try {
            const response = await axiosInstance.post("/api/auth/login", {
                username: username,
                password: password,
            });

            const token =
                response.data.token ||
                response.data.accessToken ||
                response.data.data?.token;

            if (!token) {
                throw new Error("No token received from server");
            }

            localStorage.setItem("authToken", token);
            setUser(buildUserFromToken(token));

            return response.data;
        } catch (error) {
            const status = error.response?.status;
            const errorData = error.response?.data;

            let message = "Login failed";
            if (errorData && typeof errorData === "object") {
                message =
                    errorData.message || errorData.error || errorData.detail || message;
            } else if (typeof errorData === "string") {
                message = errorData;
            }

            if (status === 400 || status === 401) {
                message = "Invalid username or password";
            }

            const loginError = new Error(message);
            loginError.status = status;
            loginError.data = errorData;
            throw loginError;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await axiosInstance.post("/api/auth/logout-time");
        } catch (error) {
            console.error("Logout API call failed:", error);
        } finally {
            localStorage.removeItem("authToken");
            setUser(null);
        }
    }, []);
    const makeAuthenticatedRequest = useCallback((config) => {
        if (!config) {
            return Promise.reject(new Error("No config provided"));
        }
        if (typeof config === "string") {
            return axiosInstance.get(config);
        }
        return axiosInstance.request(config);
    }, []);

    const contextValue = {
        user: user,
        token: user?.token || null,
        login: login,
        logout: logout,
        makeAuthenticatedRequest: makeAuthenticatedRequest,
        axiosInstance: axiosInstance,
        loading: loading,
        initializing: initializing,
        isAuthenticated: user !== null && user.token !== null,
    };

    return (
        <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};
